import { ListContactsFilter } from "@dune/types";
import { Button, Input, PaginatedDataTable, useDebounce } from "@dune/ui";
import { Plus } from "lucide-react";
import { parseAsStringEnum, useQueryState } from "nuqs";
import { useState } from "react";
import { useGetContactsList } from "../../api/queries/contacts.queries";
import { Page } from "../../component/common/Page";
import { AddContactDialog } from "../../component/contacts/AddContactDialog";
import { DeleteContactDialog } from "../../component/contacts/DeleteContactDialog";
import { UpdateContactDialog } from "../../component/contacts/UpdateContactDialog";
import PageLayoutWithTabs, {
	Tab,
	TabLayoutErrorSkeleton,
	TabLayoutSkeleton,
} from "../../component/layout/PageLayoutWithTabs";
import { listContactsColumns } from "./listContactsColumns";
import { usePagination } from "../../hooks/usePagination";

const ContactsTable = ({
	filter,
	onClickEdit,
	onClickDelete,
}: {
	filter: ListContactsFilter;
	onClickEdit: (contactId: number) => void;
	onClickDelete: (contactId: number) => void;
}) => {
	const [searchInput, setSearchInput] = useState("");
	const searchInputDebounced = useDebounce(searchInput, 300);
	const { page, limit, setPage, setLimit } = usePagination();
	const { contacts, isLoading } = useGetContactsList(
		searchInputDebounced,
		filter,
		{ page, limit },
	);
	const handlePageChange = (page: number) => {
		setPage(page);
	};
	const handleLimitChange = (limit: number) => {
		setLimit(limit);
	};

	return (
		<div className="flex flex-col gap-4 p-0">
			<Input
				placeholder="Recherche rapide"
				value={searchInput}
				onChange={(event) => setSearchInput(event.target.value)}
				className="w-sm"
			/>
			<PaginatedDataTable
				data={contacts?.data ?? []}
				columns={listContactsColumns(onClickEdit, onClickDelete)}
				noResultsText="Aucun contact trouvé"
				isLoading={isLoading}
				meta={
					contacts?.meta ?? {
						page,
						limit,
						total: 0,
						totalPages: 1,
						hasNextPage: false,
						hasPreviousPage: false,
					}
				}
				onPageChange={handlePageChange}
				onLimitChange={handleLimitChange}
			/>
		</div>
	);
};

export const ListContactsPage = () => {
	const { contacts, isLoading, error } = useGetContactsList(
		"",
		ListContactsFilter.ALL,
		{ page: 1, limit: 10 },
	);
	const tabs: Tab<ListContactsFilter>[] = [
		{ value: ListContactsFilter.ALL, label: "Tous" },
		{ value: ListContactsFilter.CONTACTS, label: "Contacts" },
		{ value: ListContactsFilter.USERS, label: "Utilisateurs" },
		{ value: ListContactsFilter.BROKERS, label: "Apporteurs" },
		{ value: ListContactsFilter.SYNDICS, label: "Syndics" },
		{ value: ListContactsFilter.CLIENTS, label: "Clients" },
		{ value: ListContactsFilter.INSURERS, label: "Assureurs" },
		{ value: ListContactsFilter.TPAS, label: "TPA" },
	];

	const [activeTab, setActiveTab] = useQueryState<ListContactsFilter>(
		"tab",
		parseAsStringEnum<ListContactsFilter>(
			Object.values(ListContactsFilter),
		).withDefault(ListContactsFilter.ALL),
	);

	const [contactToUpdateId, setContactToUpdateId] = useState<number | null>(
		null,
	);
	const [contactToDeleteId, setContactToDeleteId] = useState<number | null>(
		null,
	);

	const ContactsTableWithFilter = (filter: ListContactsFilter) => {
		return (
			<ContactsTable
				filter={filter}
				onClickEdit={(contactId: number) => setContactToUpdateId(contactId)}
				onClickDelete={(contactId: number) => setContactToDeleteId(contactId)}
			/>
		);
	};

	const tabContent: Record<ListContactsFilter, React.ReactNode> = {
		ALL: ContactsTableWithFilter(ListContactsFilter.ALL),
		CONTACTS: ContactsTableWithFilter(ListContactsFilter.CONTACTS),
		USERS: ContactsTableWithFilter(ListContactsFilter.USERS),
		BROKERS: ContactsTableWithFilter(ListContactsFilter.BROKERS),
		SYNDICS: ContactsTableWithFilter(ListContactsFilter.SYNDICS),
		CLIENTS: ContactsTableWithFilter(ListContactsFilter.CLIENTS),
		INSURERS: ContactsTableWithFilter(ListContactsFilter.INSURERS),
		TPAS: ContactsTableWithFilter(ListContactsFilter.TPAS),
	};

	const [isAddContactDialogOpened, setIsAddContactDialogOpened] =
		useState(false);
	if (isLoading) {
		return <TabLayoutSkeleton tabCount={tabs.length} />;
	}

	if (error || !contacts) {
		return (
			<TabLayoutErrorSkeleton
				message="Erreur lors du chargement des contacts."
				tabCount={tabs.length}
			/>
		);
	}

	return (
		<Page>
			<PageLayoutWithTabs
				title={"Contacts"}
				tabs={tabs}
				activeTab={activeTab}
				onTabChange={(value) => setActiveTab(value)}
				tabContent={tabContent}
				headerActions={
					<Button onClick={() => setIsAddContactDialogOpened(true)}>
						<Plus />
						Nouveau contact
					</Button>
				}
			/>
			<AddContactDialog
				open={isAddContactDialogOpened}
				onOpenChange={setIsAddContactDialogOpened}
			/>
			{contactToUpdateId && (
				<UpdateContactDialog
					contactToUpdateId={contactToUpdateId}
					open
					onOpenChange={() => setContactToUpdateId(null)}
				/>
			)}
			{contactToDeleteId && (
				<DeleteContactDialog
					contactToDeleteId={contactToDeleteId}
					open
					onOpenChange={() => setContactToDeleteId(null)}
				/>
			)}
		</Page>
	);
};
